import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { utilityService } from "../services/utility.service";
import { ApiCall } from "../helper/axios";
/**
 * Initial state
 */
const initialState = {
    activeClients: [],
    cache: null,
    loading: false,
    error: null,
    currentStatus: "active",
    currentModule: null,
};
/**
 * Async thunk: Fetch active clients
 */
export const fetchActiveClients = createAsyncThunk("utility/fetchActiveClients", async (args, { getState, rejectWithValue }) => {
    const status = args?.status || "active";
    const moduleName = args?.moduleName;
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await utilityService.getActiveClients(token || "", status, moduleName);
        return {
            clients: response.data.clients ?? [],
            cache: response.data.cache,
            moduleName
        };
    }
    catch (err) {
        return rejectWithValue(err?.message || "Failed to fetch clients");
    }
}, {
    condition: (args, { getState }) => {
        const status = args?.status || "active";
        const moduleName = args?.moduleName;
        const { utility } = getState();
        return !utility.loading &&
            (utility.activeClients.length === 0 ||
                utility.currentStatus !== status ||
                utility.currentModule !== moduleName);
    },
});
export const fetchModuleContext = createAsyncThunk("utility/fetchModuleContext", async (moduleKey, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", `/api/auth/user-context?module_key=${moduleKey}`, "", { Authorization: token || "" });
        if (response && "data" in response && response.data?.success) {
            return response.data?.data || null;
        }
        return null;
    }
    catch {
        return rejectWithValue("Failed to fetch module context");
    }
});
export const updateClientVisibilityThunk = createAsyncThunk("utility/updateClientVisibility", async ({ clientIds, client_visible }, { getState, dispatch, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        await utilityService.updateClientVisibility(clientIds, client_visible, token || "");
        // ✅ Refetch updated list
        const currentStatus = getState().utility?.currentStatus || 'active';
        const currentModule = getState().utility?.currentModule;
        dispatch(fetchActiveClients({ status: currentStatus, moduleName: currentModule || undefined }));
    }
    catch (err) {
        return rejectWithValue(err.message);
    }
});
/**
 * Utility Slice
 */
const utilitySlice = createSlice({
    name: "utility",
    initialState,
    reducers: {
        clearActiveClients: (state) => {
            state.activeClients = [];
            state.error = null;
        },
        setCacheData: (state, action) => {
            state.cache = action.payload;
        },
        clearCacheData: (state) => {
            state.cache = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch active clients
            .addCase(fetchActiveClients.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchActiveClients.fulfilled, (state, action) => {
            state.loading = false;
            state.activeClients = action.payload.clients;
            state.cache = action.payload.cache;
            state.currentStatus = action.meta.arg?.status || "active";
            if (action.payload.moduleName) {
                state.currentModule = action.payload.moduleName;
            }
        })
            .addCase(fetchActiveClients.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
            // Fetch module context
            .addCase(fetchModuleContext.fulfilled, (state, action) => {
            if (action.payload) {
                state.cache = action.payload;
            }
        })
            // Update client visibility
            .addCase(updateClientVisibilityThunk.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(updateClientVisibilityThunk.fulfilled, (state) => {
            state.loading = false;
        })
            .addCase(updateClientVisibilityThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});
// Actions
export const { clearActiveClients, setCacheData, clearCacheData } = utilitySlice.actions;
// Selectors
export const selectActiveClients = (state) => state.utility.activeClients;
export const selectUtilityLoading = (state) => state.utility.loading;
export const selectUtilityError = (state) => state.utility.error;
export default utilitySlice.reducer;
